import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'
import Image from 'next/image'
import Link from 'next/link'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import FadeIn from '@/components/ui/FadeIn'

interface ProcessGuideData {
	anchor_title?: string
	heading?: {
		title?: string
		text?: string
	}
	text?: {
		text?: string
	}
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title: string
					text: string
				}
			}[]
		}
	}[]
	image?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_disk: string
			}
		}
	}
	button?: {
		href?: string
		label?: string
	}
}

export const fields = [
	'anchor_title',
	'heading.title',
	'heading.text',
	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',
	'image.alt',
	'image.image_file.image.id',
	'image.image_file.image.filename_download',
	'text.text',
	'button.href',
	'button.label',
] as const

interface ProcessGuideProps {
	data?: ProcessGuideData
}

export default function ProcessGuide({ data }: ProcessGuideProps) {
	const { ref, isVisible } = useScrollAnimation({
		threshold: 0.05,
		rootMargin: '0px',
	})
	const sectionIdBase = data?.anchor_title || 'ProcessGuide'
	const sectionId = sectionIdBase
		.replace(/\s+/g, '-')
		.replaceAll(/[^a-zA-Z0-9-_]/g, '-')

	return (
		<section id={sectionId} data-name={data?.anchor_title || 'ProcessGuide'} ref={ref} className='ProcessGuide py-16 max-md:overflow-hidden'>
			<div className='container'>
				<div className='grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16'>
					{/* Left Column */}
					<div className='flex flex-col gap-8'>
						{/* Image */}
						{data?.image?.image_file?.image?.id && (
							<FadeIn
								isVisible={isVisible}
								delay={0}
								direction='left'
								className='relative aspect-[642/500] w-full overflow-hidden bg-neutral lg:order-2'
							>
								<Image
									src={
										getReadableImageUrl(
											data.image.image_file.image.id,
											data.image.image_file.image.filename_disk || ''
										) || ''
									}
									alt={data?.image?.alt || ''}
									fill
									className='object-cover object-center'
								/>
							</FadeIn>
						)}

						{/* Heading and Text */}
						<div className='flex flex-col lg:order-1'>
							{data?.heading?.title && (
								<FadeIn isVisible={isVisible} delay={100} direction='up'>
									<h2>{data.heading.title}</h2>
								</FadeIn>
							)}
							{data?.text?.text && (
								<FadeIn isVisible={isVisible} delay={200} direction='up'>
									<div
										className='grid gap-4'
										dangerouslySetInnerHTML={sanitizeHTML(data.text.text)}
									/>
								</FadeIn>
							)}
							{/* Desktop Button */}
							{data?.button && (
								<FadeIn
									isVisible={isVisible}
									delay={300}
									className='mt-8 lg:order-3 hidden lg:flex'
								>
									<Link href={data.button.href || '/'} className='btn'>
										{data.button.label}
									</Link>
								</FadeIn>
							)}
						</div>
					</div>

					{/* Right Column - Content Collection */}
					<div className='flex flex-col gap-8'>
						{data?.content_collection?.map((collection, index) => (
							<FadeIn
								key={collection.id}
								isVisible={isVisible}
								delay={400 + index * 100}
								direction='up'
								className='flex flex-col gap-4 bg-[#EDEDED] p-8'
							>
								{collection.site_content_collections_id.collection[0]?.item
									?.title && (
									<h3 className='font-freightBig text-3xl md:text-4xl'>
										{
											collection.site_content_collections_id.collection[0]?.item
												?.title
										}
									</h3>
								)}
								<div
									className='grid gap-4'
									dangerouslySetInnerHTML={sanitizeHTML(
										collection.site_content_collections_id.collection[1]?.item
											?.text || ''
									)}
								/>
							</FadeIn>
						))}
					</div>
				</div>
				{/* Mobile Button */}
				{data?.button && (
					<FadeIn
						isVisible={isVisible}
						delay={400 + (data?.content_collection?.length || 0) * 100}
						className='mt-8 md:mt-16 md:flex justify-center'
					>
						<Link href={data.button.href || '/'} className='btn'>
							{data.button.label}
						</Link>
					</FadeIn>
				)}
			</div>
		</section>
	)
}

